import React, { useState, useEffect } from 'react'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { Form, Button } from 'semantic-ui-react'
import FWOIteractiveTemplateRow from '../helpers/FWOIteractiveTemplateRow'
import { getInitialValues, getFields, getFieldSchema } from '../common'
import {
  FormKey,
  IInteractiveTemplateField,
  IInteractiveTemplatePage,
  IInteractiveTemplateSection,
} from '../../../models/InteractiveTemplate'
import PageTitle from '../../../components/PageTitle'
import Accordion from '../../common/Accordion'
import '../InteractiveTemplates.css'
import './EndOfProbation.css'

export const EndOfProbationTemplate = ({
  show,
  setShow,
  template,
  handleNext,
}) => {
  const [initialValues, setInitialValues] = useState({})
  const [awardAgreement, setAwardAgreement] = useState(null)
  const [validationSchema, setValidationSchema] = useState(null)
  const [termsCondition, setTermsCondition] = useState('')

  useEffect(() => {
    const schema = createYupSchema()
    setValidationSchema(schema)
  }, [])

  const createYupSchema = () => {
    setInitialValues(getInitialValues(template))
    const fields = getFields(template)

    const schema = fields.reduce((schema, field) => {
      if (field.replacement === 'AWARD') {
        return {
          ...schema,
          [field.replacement]: Yup.string().when('AWARD_OR_AGREEMENT', {
            is: (AWARD_OR_AGREEMENT) => AWARD_OR_AGREEMENT === 'Award',
            then: Yup.string().required(`${field.label} is required`),
          }),
        }
      }

      if (field.replacement === 'ENTERPRISE_AGREEMENT') {
        return {
          ...schema,
          [field.replacement]: Yup.string().when('AWARD_OR_AGREEMENT', {
            is: (AWARD_OR_AGREEMENT) =>
              AWARD_OR_AGREEMENT === 'Enterprise Agreement',
            then: Yup.string().required(`${field.label} is required`),
          }),
        }
      }

      if (
        field.replacement === 'TERMS_NAME' ||
        field.replacement === 'TERMS_DATE'
      ) {
        return {
          ...schema,
          [field.replacement]: Yup.string().when('TERMS_CONDITIONS', {
            is: (TERMS_CONDITIONS) => TERMS_CONDITIONS === 'Yes',
            then: Yup.string().required(`${field.label} is required`),
          }),
        }
      }

      return getFieldSchema(schema, field)
    }, {})

    return Yup.object().shape(schema)
  }

  const renderField = (field: IInteractiveTemplateField, index: number) => {
    if (field.replacement === 'AWARD_OR_AGREEMENT') {
      return (
        <FWOIteractiveTemplateRow
          key={index}
          {...field}
          onSelection={(value) => setAwardAgreement(value?.toLowerCase())}
        ></FWOIteractiveTemplateRow>
      )
    }

    if (field.replacement === 'AWARD' && awardAgreement !== 'award') {
      return null
    }

    if (field.replacement === 'AWARD' && awardAgreement === 'award') {
      field.placeholder = '[Choose one]'
    }

    if (
      field.replacement === 'ENTERPRISE_AGREEMENT' &&
      awardAgreement !== 'enterprise agreement'
    ) {
      return null
    }

    if (field.replacement === 'TERMS_CONDITIONS') {
      return (
        <FWOIteractiveTemplateRow
          key={index}
          {...field}
          onSelection={(value) => setTermsCondition(value?.toLowerCase())}
        ></FWOIteractiveTemplateRow>
      )
    }

    if (
      (field.replacement === 'TERMS_NAME' ||
        field.replacement === 'TERMS_DATE') &&
      termsCondition !== 'yes'
    ) {
      return null
    }

    return (
      <FWOIteractiveTemplateRow
        key={index}
        {...field}
      ></FWOIteractiveTemplateRow>
    )
  }

  const renderSection = (
    section: IInteractiveTemplateSection,
    index: number
  ) => {
    if (section?.fields) {
      return (
        <section key={index}>
          <h3>{section.name}</h3>
          {section.fields.map((field, index) => renderField(field, index))}
        </section>
      )
    } else {
      return null
    }
  }

  const renderPage = (page: IInteractiveTemplatePage, index: number) => {
    return (
      <Accordion
        key={index}
        heading={page.name}
        expanded={show}
        onClick={setShow}
      >
        {page.sections?.map((section, index) => renderSection(section, index))}
        <p className="form-submit">
          <Button className="ui is-primary button form-submit">Next</Button>
        </p>
      </Accordion>
    )
  }

  const onSubmit = (values) => {
    values[
      'EMPLOYEE_ADDRESS'
    ] = `${values['EMPLOYEE_ADDRESS_STREET']}, ${values['EMPLOYEE_ADDRESS_SUBURB']}, ${values['EMPLOYEE_ADDRESS_STATE']} ${values['EMPLOYEE_ADDRESS_POSTCODE']}`

    setInitialValues(values)
    handleNext(values, FormKey.EndOfProbation)
  }

  return (
    <>
      <PageTitle title="End of probation letter - Templates - Fair Work Ombudsman" />
      <Formik
        validationSchema={validationSchema}
        enableReinitialize
        initialValues={initialValues}
        validateOnMount={false}
        onSubmit={onSubmit}
      >
        {({ handleSubmit }) => (
          <Form
            className="ui form osp-variant large"
            onSubmit={handleSubmit}
            autoComplete="off"
          >
            <div className="accordian">
              {template?.pages?.map((page, index) => renderPage(page, index))}
            </div>
          </Form>
        )}
      </Formik>
    </>
  )
}
